import { FileNameType, IFile, ErrorType, IFileError } from './types';

export const splitFileExtension = (fileName: FileNameType) => {
    const extension = fileName.split('.').pop();
    const nameString = fileName.replace(`.${extension}`, '');
    return { nameString, extension };
};

export const isFileExist = (fileName: FileNameType, files: IFile[]) => files.some((file) => file.key === fileName);
export const isErrorExist = (fileName: FileNameType, errors: IFileError[]) => {
    const count = errors.filter((err) => err.fileName === fileName).length;
    return count > 0;
};

export const getUniqueFileName = (fileName: FileNameType, files: IFile[]) => {
    // fileName format, e.g. sample.pdf, sample(1).pdf, sample(2).pdf
    let count = files.filter((file) => file.key === fileName).length;
    if (count === 0) return fileName;

    const { nameString, extension } = splitFileExtension(fileName);
    let newNameString = nameString;
    const regex = /\((\d+)\)/;
    const match = newNameString.match(regex);

    if (match) {
        count = parseInt(match[1]);
        count++;
        newNameString = newNameString.replace(match[0], '');
    }

    let uniqueFileName = `${newNameString}(${count}).${extension}`;
    while (isFileExist(uniqueFileName, files)) {
        uniqueFileName = getUniqueFileName(uniqueFileName, files);
    }

    return uniqueFileName;
};

export const getTooSmallRejectionErr = (fileName: FileNameType, minUploadFileSize: number): IFileError => {
    return {
        fileName,
        error: 'FILE_TOO_SMALL' as ErrorType,
        metaData: { minUploadFileSize },
    };
};

export const getTooLargeRejectionErr = (fileName: FileNameType, maxUploadFileSizeInMb: number): IFileError => {
    return {
        fileName,
        error: 'FILE_TOO_LARGE' as ErrorType,
        metaData: { maxUploadFileSizeInMb },
    };
};

export const getInfectedRejectionErr = (fileName: FileNameType): IFileError => {
    return {
        fileName,
        error: 'FILE_IS_INFECTED' as ErrorType,
    };
};

export const getFileExistsError = (fileName: FileNameType): IFileError => {
    return {
        fileName,
        error: 'FILE_ALREADY_EXISTS' as ErrorType,
    };
};

export const getFileNameTooLongError = (fileName: FileNameType): IFileError => {
    return {
        fileName,
        error: 'FILE_NAME_TOO_LONG' as ErrorType,
    };
};

export const getFileNameForbiddenCharactersError = (fileName: FileNameType, regEx: RegExp): IFileError => {
    const allMatches = fileName.match(new RegExp(regEx, 'g'));
    const offendingCharacters = [...new Set(allMatches)];

    return {
        fileName,
        error: 'FILE_NAME_FORBIDDEN_CHARACTERS' as ErrorType,
        metaData: { offendingCharacters: offendingCharacters.join(' ') },
    };
};

export const getScanningRejectionErr = (fileName: FileNameType): IFileError => {
    return {
        fileName,
        error: 'SCANNING_ERROR' as ErrorType,
    };
};

export const getTooManyFilesRejectionErr = (fileName: FileNameType, totalFilesLimit = 10): IFileError => {
    return {
        fileName,
        error: 'TOO_MANY_FILES' as ErrorType,
        metaData: { totalFilesLimit },
    };
};

export const getInvalidTypeRejectionErr = (fileName: FileNameType): IFileError => {
    return {
        fileName,
        error: 'FILE_INVALID_TYPE' as ErrorType,
    };
};

export const getConfigRejectionErr = (fileName: FileNameType, errMessage: string): IFileError => {
    return {
        fileName,
        error: 'CONFIG_ERROR' as ErrorType,
        metaData: {
            message: errMessage,
        },
    };
};

export const getDeletingRejectionErr = (fileName: FileNameType, errMessage: string): IFileError => {
    return {
        fileName,
        error: 'DELETING_ERROR' as ErrorType,
        metaData: {
            message: errMessage,
        },
    };
};

export const getUploadRejectionErr = (fileName: FileNameType, errMessage: string): IFileError => {
    return {
        fileName,
        error: 'UPLOAD_ERROR' as ErrorType,
        metaData: {
            message: errMessage,
        },
    };
};

export const getStateRejectionErr = (fileName: FileNameType, errMessage: string): IFileError => {
    return {
        fileName,
        error: 'STATE_ERROR' as ErrorType,
        metaData: {
            message: errMessage,
        },
    };
};

export const isDefined = (value: any) => {
    return value !== undefined && value !== null;
};
